import { KnownError } from "@/api/client";
import { useSubmitSupportTicket } from "@/api/support";
import { imageUrls } from "@/constants/url";
import { SupportTextarea } from "@/routes/support/components/support-textarea";
import {
  clearSupportRequestId,
  createNewSupportRequestId,
  loadOrCreateSupportRequestId,
} from "@/routes/support/lib/request-id";
import { SUPPORT_FORM_LIMITS, type SupportLocale, supportCopy } from "@/routes/support/lib/support-copy";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Typography,
} from "@backpac/monica";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface SupportFormValues {
  name: string;
  email: string;
  subject: string;
  body: string;
  // Honeypot: real users never see or fill this field.
  website: string;
}

function buildSchema(locale: SupportLocale) {
  const copy = supportCopy[locale];
  return z.object({
    name: z.string().trim().min(1, copy.validation.nameRequired).max(SUPPORT_FORM_LIMITS.name, copy.validation.nameMax),
    email: z
      .string()
      .trim()
      .max(SUPPORT_FORM_LIMITS.email, copy.validation.emailMax)
      .email(copy.validation.emailInvalid),
    subject: z
      .string()
      .trim()
      .min(1, copy.validation.subjectRequired)
      .max(SUPPORT_FORM_LIMITS.subject, copy.validation.subjectMax),
    body: z.string().trim().min(1, copy.validation.bodyRequired).max(SUPPORT_FORM_LIMITS.body, copy.validation.bodyMax),
    website: z.string().optional().default(""),
  });
}

type SubmitOutcome =
  | { kind: "success"; reference: string }
  | { kind: "ambiguous" }
  | { kind: "rejected"; message: string };

export function SupportPage() {
  const [locale, setLocale] = useState<SupportLocale>("ko");
  const copy = supportCopy[locale];
  const [outcome, setOutcome] = useState<SubmitOutcome | null>(null);
  // Ambiguous failures (network/5xx) must reuse the same requestId on retry so
  // the server can dedupe; only a confirmed success mints a fresh one.
  const [requestId, setRequestId] = useState(() => loadOrCreateSupportRequestId());

  const schema = useMemo(() => buildSchema(locale), [locale]);
  const form = useForm<SupportFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", subject: "", body: "", website: "" },
  });

  const { mutateAsync, isPending } = useSubmitSupportTicket();

  const handleSubmit = async (values: SupportFormValues) => {
    setOutcome(null);
    try {
      const response = await mutateAsync({
        name: values.name,
        email: values.email,
        subject: values.subject,
        body: values.body,
        requestId,
        website: values.website,
      });
      // Confirmed success: safe to retire this requestId and mint a new one
      // for any future, unrelated message.
      clearSupportRequestId();
      setRequestId(createNewSupportRequestId());
      setOutcome({ kind: "success", reference: response.reference });
    } catch (error) {
      if (KnownError.is(error) && error.status >= 400 && error.status < 500) {
        // Server definitively rejected this request (validation/spam/rate
        // limit) — the ticket was not created, so it's still safe to reuse
        // requestId once the user fixes the issue and retries.
        setOutcome({ kind: "rejected", message: error.message || copy.fallbackErrorBody });
      } else {
        // Network error, timeout, or 5xx: we genuinely don't know whether the
        // server persisted the ticket. Never claim success, and keep the same
        // requestId so a retry can't create a duplicate.
        setOutcome({ kind: "ambiguous" });
      }
    }
  };

  const handleSubmitAnother = () => {
    form.reset({ name: "", email: "", subject: "", body: "", website: "" });
    setOutcome(null);
  };

  return (
    <div className="min-h-screen w-full bg-backgroundLight flex flex-col items-center px-4 py-12 desktop:py-20">
      <Helmet>
        <title>{`${copy.eyebrow} | BackPac`}</title>
        <meta name="theme-color" content="#fcfaf5" />
      </Helmet>

      <div className="w-full max-w-[560px] flex flex-col gap-8">
        <div className="flex items-center justify-between">
          <img src={imageUrls.logo.desktop} alt="BackPac" className="h-6 w-auto" />
          <Button
            type="button"
            variant="outlined"
            size="sm"
            onClick={() => setLocale(locale === "ko" ? "en" : "ko")}
            aria-label="Toggle language"
          >
            {copy.languageToggleLabel}
          </Button>
        </div>

        <div className="bg-white rounded-[20px] shadow-standard p-6 desktop:p-10 flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <Typography asChild size="xs" weight={600} color="brandTangerine">
              <span>{copy.eyebrow}</span>
            </Typography>
            <Typography asChild size="2xl" font="unbounded" color="textPrimary">
              <h1>{copy.title}</h1>
            </Typography>
            <Typography asChild size="sm" color="textSecondary" className="leading-[1.5]">
              <p>{copy.description}</p>
            </Typography>
          </div>

          {outcome?.kind === "success" ? (
            <SupportOutcomePanel
              tone="blue"
              icon="i-bp-checked"
              title={copy.successTitle}
              body={copy.successBody(outcome.reference)}
              action={
                <Button type="button" variant="secondary" size="md" onClick={handleSubmitAnother}>
                  {copy.submitAnother}
                </Button>
              }
            />
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-5" noValidate>
                {outcome?.kind === "ambiguous" ? (
                  <SupportOutcomePanel
                    tone="orange"
                    icon="i-bp-warning"
                    title={copy.ambiguousTitle}
                    body={copy.ambiguousBody}
                  />
                ) : null}
                {outcome?.kind === "rejected" ? (
                  <SupportOutcomePanel
                    tone="orange"
                    icon="i-bp-exclamation"
                    title={copy.rejectedTitle}
                    body={outcome.message}
                  />
                ) : null}

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{copy.nameLabel}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={copy.namePlaceholder} maxLength={SUPPORT_FORM_LIMITS.name} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{copy.emailLabel}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder={copy.emailPlaceholder}
                          maxLength={SUPPORT_FORM_LIMITS.email}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{copy.subjectLabel}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={copy.subjectPlaceholder}
                          maxLength={SUPPORT_FORM_LIMITS.subject}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="body"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{copy.bodyLabel}</FormLabel>
                      <FormControl>
                        <SupportTextarea
                          {...field}
                          placeholder={copy.bodyPlaceholder}
                          maxLength={SUPPORT_FORM_LIMITS.body}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Honeypot: visually and semantically hidden from real users/screen readers. */}
                <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
                  <label htmlFor="support-website">Website</label>
                  <input
                    id="support-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    {...form.register("website")}
                  />
                </div>

                <Button type="submit" size="lg" className="w-full" disabled={isPending} loading={isPending}>
                  {isPending ? copy.submitting : copy.submit}
                </Button>
              </form>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
}

function SupportOutcomePanel({
  tone,
  icon,
  title,
  body,
  action,
}: {
  tone: "blue" | "orange";
  icon: string;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  const toneClasses = tone === "blue" ? "bg-colorBlueLight text-colorBlue" : "bg-colorOrangeLight text-colorOrange";

  return (
    <div className={`flex flex-col gap-3 rounded-[16px] p-5 ${toneClasses}`}>
      <div className="flex items-start gap-3">
        <i className={`inline-block size-5 flex-shrink-0 ${icon}`} />
        <div className="flex flex-col gap-1">
          <Typography
            asChild
            size="sm"
            weight={600}
            className={tone === "blue" ? "text-colorBlue" : "text-colorOrange"}
          >
            <p>{title}</p>
          </Typography>
          <Typography asChild size="xs" className={tone === "blue" ? "text-colorBlue" : "text-colorOrange"}>
            <p className="leading-[1.5]">{body}</p>
          </Typography>
        </div>
      </div>
      {action != null ? <div>{action}</div> : null}
    </div>
  );
}
